import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Tooltip,
  Grid,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  LinearProgress,
  Link,
  Chip,
  Avatar,
  Switch,
} from '@mui/material';
import { 
  Upload, 
  Download, 
  ImageIcon, 
  Sparkles, 
  HelpCircle,
  CheckCircle,
  X,
  Maximize,
  Settings,
  Palette,
  Lightbulb,
  Sun,
  Zap,
  AlertCircle,
  Wand2,
  Plus,
  Trash2,
  ZoomIn,
  ZoomOut,
  RefreshCw,
} from 'lucide-react';
import PageHeader from './PageHeader';
import ImageControls from './ImageControls';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiUtils';
import aiClient from '../api/clients/AiClient';
import imageClient from '../api/clients/ImageClient';
import { useAIModels } from '../hooks/useAIModels';
import WordPressClient from '../utils/WordPressClient';
import CompressionDialog from './CompressionDialog';
import { compressImage, compressImageLossless } from '../utils/imageCompression';
import JSZip from 'jszip';
import { imageModels } from './ImageControls';


const ImageGenerator = () => {

  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // Basic state
  const [prompts, setPrompts] = useState('');
  const [model, setModel] = useState('fal-ai/flux/schnell');
  const [dimension, setDimension] = useState('square_hd');
  const [useAIEnhancement, setUseAIEnhancement] = useState(true); // Re-add state
  const [safetyCheckerEnabled, setSafetyCheckerEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });
  const [generatedImage, setGeneratedImage] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [wordPressSites, setWordPressSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  const [compressionDialogOpen, setCompressionDialogOpen] = useState(false);
  const [compressionAction, setCompressionAction] = useState('download');
  const [pendingImages, setPendingImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  const [isImprovingPrompt, setIsImprovingPrompt] = useState(false);
  const [promptManuallyAccepted, setPromptManuallyAccepted] = useState(false); // Track manual acceptance

  // Add new state for zoom functionality
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });

  // Style presets data
  const stylePresets = [
    { 
      id: 1, 
      name: "Photorealistic", 
      prompt: "photorealistic, highly detailed, 8k, professional photography", 
      color: "#3B82F6",
      icon: "📷"
    },
    { 
      id: 2, 
      name: "Oil Painting", 
      prompt: "oil painting, textured canvas, detailed brushstrokes, artistic, vibrant colors", 
      color: "#10B981",
      icon: "🎨"
    },
    { 
      id: 3, 
      name: "Anime", 
      prompt: "anime style, cel shaded, vibrant, detailed, studio ghibli inspired", 
      color: "#F97316",
      icon: "✨"
    },
    { 
      id: 4, 
      name: "Cinematic", 
      prompt: "cinematic, dramatic lighting, movie scene, high budget, 35mm film", 
      color: "#8B5CF6",
      icon: "🎬"
    },
    { 
      id: 5, 
      name: "Watercolor", 
      prompt: "watercolor painting, soft colors, flowing, artistic, hand-painted", 
      color: "#EC4899",
      icon: "💦"
    },
    { 
      id: 6, 
      name: "3D Render", 
      prompt: "3D render, octane render, highly detailed, volumetric lighting, 8k", 
      color: "#6366F1",
      icon: "🧊"
    },
    { 
      id: 7, 
      name: "Pixel Art", 
      prompt: "pixel art, 16-bit, retro game style, pixelated, vibrant colors", 
      color: "#EF4444",
      icon: "👾"
    },
    { 
      id: 8, 
      name: "Neon", 
      prompt: "neon lighting, cyberpunk, dark background, glowing, vibrant colors", 
      color: "#06B6D4",
      icon: "💫"
    }
  ];

  const [notification, setNotification] = useState({ 
    open: false,
    severity: 'success',
    message: '' 
  });

  // Auth and API hooks
  const { authenticatedRequest, error: authError } = useAuth();
  const { models: availableModels, loading: modelsLoading, error: modelsError } = useAIModels(authenticatedRequest);

  // Subject categories to help users build prompts
  const subjectCategories = [
    { id: 1, name: "Landscape", prompt: "landscape", color: "#047857" },
    { id: 2, name: "Portrait", prompt: "portrait of a person", color: "#7C3AED" },
    { id: 3, name: "Animal", prompt: "animal", color: "#F59E0B" },
    { id: 4, name: "Food", prompt: "food photography", color: "#EF4444" },
    { id: 5, name: "Architecture", prompt: "architecture", color: "#3B82F6" },
    { id: 6, name: "Abstract", prompt: "abstract art", color: "#EC4899" },
    { id: 7, name: "Product", prompt: "product photography", color: "#6366F1" },
    { id: 8, name: "Nature", prompt: "nature", color: "#10B981" }
  ];
  
  // Lighting and mood options
  const lightingMoods = [
    { id: 1, name: "Sunny", prompt: "bright sunlight, sunny day", color: "#F59E0B" },
    { id: 2, name: "Dramatic", prompt: "dramatic lighting, high contrast", color: "#7C3AED" },
    { id: 3, name: "Soft", prompt: "soft lighting, gentle shadows", color: "#EC4899" },
    { id: 4, name: "Night", prompt: "night scene, moonlight", color: "#1E40AF" },
    { id: 5, name: "Studio", prompt: "professional studio lighting", color: "#3B82F6" },
    { id: 6, name: "Golden Hour", prompt: "golden hour lighting, warm tones", color: "#D97706" },
    { id: 7, name: "Neon", prompt: "neon lighting, vibrant glow", color: "#10B981" },
    { id: 8, name: "Moody", prompt: "moody atmosphere, fog", color: "#6B7280" }
  ];
  
  const [negativePrompt, setNegativePrompt] = useState('');
  const [showPromptAssistant, setShowPromptAssistant] = useState(false);

  // Add a new state for bulk mode
  const [bulkMode, setBulkMode] = useState(false);

  // Add a state for bulk prompts (array of objects)
  const [bulkPrompts, setBulkPrompts] = useState([
    { id: Date.now(), prompt: '', model: imageModels[0].value, dimension: dimension }
  ]);

  // Add a reference to the file input
  const fileInputRef = useRef(null);

  // Add a new state for the image details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedImageDetails, setSelectedImageDetails] = useState(null);

  // Add new state for AI-improved prompt
  const [improvedPrompt, setImprovedPrompt] = useState(null);
  const [originalPrompt, setOriginalPrompt] = useState('');

  // Add a function to handle opening the image details dialog
  const handleOpenImageDetails = (image) => {
    setSelectedImageDetails(image);
    setDetailsDialogOpen(true);
  };

  // Add a function to handle CSV import
  const handleCsvImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset the file input value so the same file can be selected again
    event.target.value = '';
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;
        const lines = csvContent.split('\n');
        
        // Skip header row if it exists
        const startIndex = lines[0].toLowerCase().includes('prompt') ? 1 : 0;
        
        const newPrompts = [];
        for (let i = startIndex; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          // Handle CSV with or without headers
          let prompt, promptModel, promptDimension;
          
          if (line.includes(',')) {
            // Try to parse as CSV with multiple columns
            const columns = line.split(',').map(col => col.trim());
            
            prompt = columns[0];
            promptModel = columns[1] || model;
            promptDimension = columns[2] || dimension;
            
            // Validate model and dimension
            if (promptModel && !imageModels.some(m => m.value === promptModel)) {
              promptModel = imageModels[0].value; // Fallback to default if invalid
            }
            
            const validDimensions = ["square_hd", "portrait_4_3", "portrait_16_9", "landscape_4_3", "landscape_16_9"];
            if (promptDimension && !validDimensions.includes(promptDimension)) {
              promptDimension = dimension; // Fallback to default if invalid
            }
          } else {
            // Just a single prompt per line
            prompt = line;
            promptModel = model;
            promptDimension = dimension;
          }
          
          if (prompt) {
            newPrompts.push({
              id: Date.now() + i,
              prompt,
              model: promptModel,
              dimension: promptDimension
            });
          }
        }
        
        if (newPrompts.length > 0) {
          setBulkPrompts(newPrompts);
          setNotification({
            open: true,
            severity: 'success',
            message: `Successfully imported ${newPrompts.length} prompts from CSV`
          });
        } else {
          setNotification({
            open: true,
            severity: 'error',
            message: 'No valid prompts found in the CSV file'
          });
        }
      } catch (err) {
        console.error('Error parsing CSV:', err);
        setNotification({
          open: true,
          severity: 'error',
          message: 'Failed to parse CSV file. Please check the format.'
        });
      }
    };
    
    reader.onerror = () => {
      setNotification({
        open: true,
        severity: 'error',
        message: 'Error reading the file'
      });
    };
    
    reader.readAsText(file);
  };

  const handleCompressedDownload = async (images, isLossless, options = {}) => {
    try {
      if (images.length === 1) {
        const response = await fetch(images[0].url);
        const blob = await response.blob();
        const compressedBlob = isLossless ? 
          await compressImageLossless(blob) :
          await compressImage(blob, options);
  
        const url = window.URL.createObjectURL(compressedBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `compressed-image-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const zip = new JSZip();
        for (const image of images) {
          const response = await fetch(image.url);
          const blob = await response.blob();
          const compressedBlob = isLossless ? 
            await compressImageLossless(blob) :
            await compressImage(blob, options);
          
          const filename = `compressed-${Date.now()}-${image.prompt.slice(0, 30)}.png`;
          zip.file(filename, compressedBlob);
        }
  
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `compressed-images-${Date.now()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Compression/download error:', error);
      throw error;
    }
  };
  
  const handleCompressedUpload = async (images, wpClient, isLossless, options = {}) => {
    const results = [];
    for (const image of images) {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const compressedBlob = isLossless ? 
        await compressImageLossless(blob) :
        await compressImage(blob, options);
  
      const filename = `compressed-${Date.now()}-${image.prompt.slice(0, 30)}.png`;
      const result = await wpClient.uploadMedia(compressedBlob, filename);
      results.push(result);
    }
    return results;
  };
  
  useEffect(() => {
      fetchWordPressSites();
    }, []);
  
  useEffect(() => {
    const initializeServices = async () => {
      try {
        const keys = await authenticatedRequest(() => apiClient.getApiKeys());
        aiClient.setApiKeys(keys);
        aiClient.updateModelConfigs(availableModels);
        imageClient.setAvailableModels(availableModels);
        imageClient.setAiClient(aiClient);
        imageClient.setApiKeys(keys);
      } catch (error) {
        console.error('Error initializing services:', error);
        setError('Failed to initialize services: ' + error.message);
      }
    };

    if (availableModels.length > 0) {
      initializeServices();
    }
  }, [availableModels, authenticatedRequest]);

  const getAuxiliaryModel = () => {
    const stored = localStorage.getItem('globalAiModel');
    if (stored && availableModels.some(m => m.value === stored)) {
      return stored;
    }
    return availableModels[0]?.value;
  };

  // Function to append subject to prompt
  const appendSubjectToPrompt = (subjectPrompt) => {
    // If there's no text yet, just set the subject as the prompt
    if (!prompts.trim()) {
      setPrompts(subjectPrompt);
      return;
    }
    
    // Split into lines for multi-line prompts
    const promptLines = prompts.split('\n');
    const lastLine = promptLines[promptLines.length - 1];
    
    // Check if subject is already in the prompt to avoid duplication
    if (!lastLine.includes(subjectPrompt)) {
      // Add the subject at the beginning of the prompt if possible
      const words = lastLine.split(' ');
      if (words.length <= 3 && !lastLine.includes(',')) {
        // If prompt is very short, replace it with subject + existing text
        promptLines[promptLines.length - 1] = `${subjectPrompt} ${lastLine}`;
      } else {
        // Otherwise add with comma
        promptLines[promptLines.length - 1] = `${lastLine}, ${subjectPrompt}`;
      }
      setPrompts(promptLines.join('\n'));
    }
  };
  
  // Function to append lighting/mood to prompt
  const appendLightingToPrompt = (lightingPrompt) => {
    // Get current prompt text
    let currentPrompts = prompts;
    
    // If there's no text yet, just set the lighting as the prompt
    if (!currentPrompts.trim()) {
      setPrompts(lightingPrompt);
      return;
    }
    
    // Split into lines for multi-line prompts
    const promptLines = currentPrompts.split('\n');
    const lastLine = promptLines[promptLines.length - 1];
    
    // Check if lighting is already in the prompt to avoid duplication
    if (!lastLine.includes(lightingPrompt)) {
      // Add the lighting with a comma separator
      const updatedLastLine = lastLine.trim().endsWith(',') || lastLine.trim() === '' 
        ? `${lastLine} ${lightingPrompt}` 
        : `${lastLine}, ${lightingPrompt}`;
      
      promptLines[promptLines.length - 1] = updatedLastLine;
      setPrompts(promptLines.join('\n'));
    }
  };
  
  // Function to handle negative prompt
  const handleNegativePromptChange = (e) => {
    setNegativePrompt(e.target.value);
  };

  const handleGenerate = async () => {
    if (bulkMode) {
      // Bulk mode generation
      const validPrompts = bulkPrompts.filter(item => item.prompt.trim());
      
      if (validPrompts.length === 0) {
        setError('Please enter at least one valid image description');
        return;
      }
      
      if (validPrompts.length > 50) {
        setError('Maximum 50 images allowed per batch');
        return;
      }

      setLoading(true);
      setError(null);
      setGeneratedImages([]);
      setSelectedImages([]);
      setGenerationProgress({ current: 0, total: validPrompts.length });

      try {
        const images = [];
        for (let i = 0; i < validPrompts.length; i++) {
          const promptItem = validPrompts[i];
          try {
            // Sanitize the prompt
            const sanitizedPrompt = promptItem.prompt
              .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
              .replace(/javascript:/gi, '')
              .replace(/on\w+=/gi, '')
              .trim()
              .slice(0, 1000);
            
            const imageData = await imageClient.generateImage({
              prompt: sanitizedPrompt,
              model: promptItem.model || model, // Use item-specific model or fallback to global
              dimension: promptItem.dimension || dimension, // Use item-specific dimension or fallback to global
              enhancePrompt: useAIEnhancement,
              enableSafetyFilter: safetyCheckerEnabled,
              negative_prompt: negativePrompt || undefined,
              context: {
                articleTitle: sanitizedPrompt
              }
            });

            if (imageData?.image_url) {
              images.push({
                id: Date.now() + i,
                url: imageData.image_url,
                prompt: sanitizedPrompt,
                status: 'success',
                model: promptItem.model,
                dimension: promptItem.dimension,
                enhancePrompt: useAIEnhancement,
                safetyFilter: safetyCheckerEnabled,
                timestamp: new Date().toISOString()
              });
            }
          } catch (err) {
            console.error(`Error generating image for prompt: ${promptItem.prompt}`, err);
            images.push({
              id: Date.now() + i,
              prompt: promptItem.prompt,
              status: 'error',
              error: err.message,
              model: promptItem.model,
              dimension: promptItem.dimension,
              enhancePrompt: useAIEnhancement,
              safetyFilter: safetyCheckerEnabled,
              timestamp: new Date().toISOString()
            });
          }
          setGenerationProgress(prev => ({ ...prev, current: i + 1 }));
          setGeneratedImages([...images]);
        }
      } catch (err) {
        console.error('Generation error:', err);
        setError(err.message || 'Failed to generate images');
      } finally {
        setLoading(false);
      }
    } else {
      // Single mode generation (existing code)
    const promptList = prompts.split('\n').filter(p => p.trim());
    if (promptList.length === 0) {
      setError('Please enter at least one image description');
      return;
    }
    if (promptList.length > 50) {
      setError('Maximum 50 images allowed per batch');
      return;
    }
    
  const sanitizedPrompts = promptList.map(prompt => {
    const sanitized = prompt
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+=/gi, '')
      .trim();
    
    return sanitized.slice(0, 1000);
  });
 
  const validPrompts = sanitizedPrompts.filter(p => p.length > 0);
  if (validPrompts.length === 0) {
    setError('Invalid prompts detected. Please enter valid image descriptions.');
    return;
  }

    setLoading(true);
    setError(null);
    setGeneratedImages([]);
    setSelectedImages([]);
    setGenerationProgress({ current: 0, total: promptList.length });

    try {
      const images = [];
      for (let i = 0; i < promptList.length; i++) {
        const prompt = promptList[i];
        
        // Determine enhancement based on manual acceptance
        let enhanceForThisImage = useAIEnhancement;
        if (promptManuallyAccepted) {
          enhanceForThisImage = false; // Override checkbox if prompt was manually accepted
        }

        try {
          const imageData = await imageClient.generateImage({
            prompt,
            model,
            dimension,
            enhancePrompt: enhanceForThisImage, // Use the determined value
            enableSafetyFilter: safetyCheckerEnabled,
            negative_prompt: negativePrompt || undefined,
            context: {
              articleTitle: prompt
            }
          });

          if (imageData?.image_url) {
            images.push({
              id: Date.now() + i,
              url: imageData.image_url,
              prompt: prompt,
              status: 'success',
              model: model,
              dimension: dimension,
              enhancePrompt: enhanceForThisImage, // Store the actual setting used
              safetyFilter: safetyCheckerEnabled,
              timestamp: new Date().toISOString()
            });
          }
        } catch (err) {
          console.error(`Error generating image for prompt: ${prompt}`, err);
          images.push({
            id: Date.now() + i,
            prompt: prompt,
            status: 'error',
            error: err.message,
            model: model,
            dimension: dimension,
            enhancePrompt: enhanceForThisImage, // Store the actual setting used
            safetyFilter: safetyCheckerEnabled,
            timestamp: new Date().toISOString()
          });
        }
        setGenerationProgress(prev => ({ ...prev, current: i + 1 }));
        setGeneratedImages([...images]);
      }
      // Reset the manual acceptance flag after generation logic is complete
      setPromptManuallyAccepted(false);
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message || 'Failed to generate images');
    } finally {
      setLoading(false);
    }
  }
};

  const handleSelectImage = (imageId) => {
    setSelectedImages(prev => {
      if (prev.includes(imageId)) {
        return prev.filter(id => id !== imageId);
      }
      return [...prev, imageId];
    });
  };

  const handleSelectAll = () => {
    if (selectedImages.length === generatedImages.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(generatedImages.filter(img => img.status === 'success').map(img => img.id));
    }
  };



  const fetchWordPressSites = async () => {
    try {
      const sitesData = await authenticatedRequest(() => 
        apiClient.getWordPressSites()
      );
      setWordPressSites(sitesData);
      if (sitesData.length > 0) {
        setSelectedSite(sitesData[0].id);
      }
    } catch (error) {
      console.error('Error fetching WordPress sites:', error);
      setError('Failed to fetch WordPress sites');
    }
  };

 

  const handleConfirmUpload = async () => {
    if (!selectedSite) {
      setUploadError('Please select a WordPress site');
      return;
    }
  
    setUploadLoading(true);
    setUploadError(null);
  
    try {
      const site = wordPressSites.find(s => s.id === selectedSite);
      if (!site) {
        throw new Error('Selected site not found');
      }
  
      const wpClient = new WordPressClient(site);
      const credentials = await authenticatedRequest(() => 
        apiClient.getWordPressCredentials(selectedSite)
      );
      wpClient.setCredentials(credentials.username, credentials.password);
  
      const imagesToUpload = generatedImages.length === 1 ? 
        [generatedImages[0]] : 
        generatedImages.filter(img => selectedImages.includes(img.id) && img.status === 'success');
  
      if (imagesToUpload.length === 0) {
        throw new Error('No valid images selected for upload');
      }
  
      // Initialize progress
      setUploadProgress({ current: 0, total: imagesToUpload.length });
  
      const uploadResults = [];
      const failures = [];
  
      for (let i = 0; i < imagesToUpload.length; i++) {
        const image = imagesToUpload[i];
        try {
          const response = await fetch(image.url);
          if (!response.ok) {
            failures.push(`Failed to download image: ${image.prompt}`);
            continue;
          }
  
          const blob = await response.blob();
          const filename = `generated-image-${Date.now()}-${image.prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '-')}.png`;
  
          const result = await wpClient.uploadMedia(blob, filename);
          if (result?.id) {
            uploadResults.push(result);
          }
        } catch (err) {
          console.error(`Failed to upload image: ${image.prompt}`, err);
          failures.push(image.prompt);
        }
        
        // Update progress
        setUploadProgress(prev => ({ ...prev, current: i + 1 }));
      }
  
      setUploadDialogOpen(false);
  
      if (failures.length === 0) {
        setNotification({
          open: true,
          severity: 'success',
          message: `Successfully uploaded ${uploadResults.length} image${uploadResults.length > 1 ? 's' : ''} to WordPress`
        });
      } else if (uploadResults.length > 0) {
        setNotification({
          open: true,
          severity: 'warning',
          message: `Uploaded ${uploadResults.length} image${uploadResults.length > 1 ? 's' : ''}, but ${failures.length} failed`
        });
      } else {
        throw new Error('All uploads failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err.message || 'Failed to upload images to WordPress');
    } finally {
      setUploadLoading(false);
      setUploadProgress({ current: 0, total: 0 }); // Reset progress
    }
  };

  const handleDownloadWithCompression = async (compressionSettings) => {
    try {
      setLoading(true);
      const imagesToDownload = generatedImages.length === 1 ? 
        [generatedImages[0]] : 
        generatedImages.filter(img => selectedImages.includes(img.id) && img.status === 'success');
  
      if (imagesToDownload.length === 0) {
        throw new Error('No valid images selected for download');
      }

      // Display a notification for multiple images to indicate download started
      if (imagesToDownload.length > 1) {
        setNotification({
          open: true,
          severity: 'info',
          message: `Preparing ${imagesToDownload.length} images for download...`
        });
        // Initialize download progress
        setDownloadProgress({ current: 0, total: imagesToDownload.length });
      }
  
      if (imagesToDownload.length === 1) {
        const image = imagesToDownload[0];
        const response = await fetch(image.url);
        const blob = await response.blob();
        
        const compressedBlob = compressionSettings.type === 'none' ? 
          blob : 
          compressionSettings.type === 'lossless' ? 
            await compressImageLossless(blob, {
              outputFormat: compressionSettings.options?.outputFormat || 'png'
            }) :
            await compressImage(blob, {
              maxSizeMB: compressionSettings.options.maxSizeMB,
              initialQuality: compressionSettings.options.initialQuality,
              outputFormat: compressionSettings.options.outputFormat || 'png'
            });
  
        const url = window.URL.createObjectURL(compressedBlob);
        const link = document.createElement('a');
        link.href = url;
        const ext = compressionSettings.options?.outputFormat || 'png';
        link.download = `compressed-image-${Date.now()}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      }
  
      const zip = new JSZip();
      
      for (let i = 0; i < imagesToDownload.length; i++) {
        const image = imagesToDownload[i];
        const response = await fetch(image.url);
        const blob = await response.blob();
        
        const compressedBlob = compressionSettings.type === 'none' ? 
          blob : 
          compressionSettings.type === 'lossless' ? 
            await compressImageLossless(blob, {
              outputFormat: compressionSettings.options?.outputFormat || 'png'
            }) :
            await compressImage(blob, {
              maxSizeMB: compressionSettings.options.maxSizeMB,
              initialQuality: compressionSettings.options.initialQuality,
              outputFormat: compressionSettings.options.outputFormat || 'png'
            });
  
        const ext = compressionSettings.options?.outputFormat || 'png';
        const filename = `compressed-${Date.now()}-${image.prompt.slice(0, 30)}.${ext}`;
        zip.file(filename, compressedBlob);
        
        // Update progress after each image is processed
        setDownloadProgress({ current: i + 1, total: imagesToDownload.length });
        
        // Update notification every few images to avoid too many notifications
        if (imagesToDownload.length > 5 && (i + 1) % Math.ceil(imagesToDownload.length / 5) === 0) {
          setNotification({
            open: true,
            severity: 'info',
            message: `Processing image ${i + 1} of ${imagesToDownload.length}...`
          });
        }
      }
      
      // Show notification when zip creation starts for large batches
      if (imagesToDownload.length > 5) {
        setNotification({
          open: true,
          severity: 'info',
          message: 'Creating zip file... This may take a moment.'
        });
      }
  
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
        // Add progress callback for zip generation
        onUpdate: (metadata) => {
          if (metadata.percent) {
            const percent = Math.floor(metadata.percent);
            if (percent % 20 === 0) { // Update every 20%
              setNotification({
                open: true,
                severity: 'info',
                message: `Creating zip file: ${percent}% complete...`
              });
            }
          }
        }
      });
      
      const zipUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = zipUrl;
      link.download = `compressed-images-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(zipUrl);
  
      setNotification({
        open: true,
        severity: 'success',
        message: `Successfully compressed and downloaded ${imagesToDownload.length} images`
      });
  
    } catch (err) {
      console.error('Download error:', err);
      setNotification({
        open: true,
        severity: 'error',
        message: err.message || 'Failed to download images'
      });
    } finally {
      setLoading(false);
      setCompressionDialogOpen(false);
      // Reset download progress
      setDownloadProgress({ current: 0, total: 0 });
    }
  };
  
  const handleUploadWithCompression = async (compressionSettings) => {
    try {
      setUploadLoading(true);
      const imagesToUpload = pendingImages;
  
      if (!imagesToUpload?.length) {
        throw new Error('No images selected for upload');
      }
  
      const site = wordPressSites.find(s => s.id === selectedSite);
      if (!site) throw new Error('Selected site not found');
  
      const wpClient = new WordPressClient(site);
      const credentials = await authenticatedRequest(() => 
        apiClient.getWordPressCredentials(selectedSite)
      );
      wpClient.setCredentials(credentials.username, credentials.password);
  
      setUploadProgress({ current: 0, total: imagesToUpload.length });
  
      const uploadResults = [];
      for (let i = 0; i < imagesToUpload.length; i++) {
        const image = imagesToUpload[i];
        try {
          const response = await fetch(image.url);
          const blob = await response.blob();
          
          // Apply compression with the correct options
          const compressedBlob = compressionSettings.type === 'none' ? 
            blob : 
            compressionSettings.type === 'lossless' ? 
              await compressImageLossless(blob, {
                outputFormat: compressionSettings.options?.outputFormat || 'png'
              }) :
              await compressImage(blob, {
                maxSizeMB: compressionSettings.options.maxSizeMB,
                initialQuality: compressionSettings.options.initialQuality,
                outputFormat: compressionSettings.options.outputFormat || 'png'
              });
  
          const ext = compressionSettings.options?.outputFormat || 'png';
          const filename = `compressed-image-${Date.now()}-${image.prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '-')}.${ext}`;
          
          const result = await wpClient.uploadMedia(compressedBlob, filename);
          if (result?.id) {
            uploadResults.push(result);
          }
          setUploadProgress(prev => ({ ...prev, current: i + 1 }));
        } catch (err) {
          console.error(`Failed to upload image: ${image.prompt}`, err);
        }
      }
  
      if (uploadResults.length > 0) {
        setNotification({
          open: true,
          severity: 'success',
          message: `Successfully uploaded ${uploadResults.length} compressed images`
        });
      } else {
        throw new Error('Failed to upload any images');
      }
  
    } catch (err) {
      console.error('Upload error:', err);
      setNotification({
        open: true,
        severity: 'error',
        message: err.message || 'Failed to upload images'
      });
    } finally {
      setUploadLoading(false);
      setUploadProgress({ current: 0, total: 0 });
      setCompressionDialogOpen(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedRequest(() =>
        apiClient.generateImage({
          prompt,
          model,
          dimension,
          useCustomPrompt: !useAIEnhancement,
          enable_safety_checker: safetyCheckerEnabled
        })
      );

      if (response.image_url) {
        setGeneratedImage(response.image_url);
      } else {
        setError('No image was generated');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  // Function to append style to prompt
  const appendStyleToPrompt = (stylePrompt) => {
    // Get current prompt text
    let currentPrompts = prompts;
    
    // If there's no text yet, just set the style as the prompt
    if (!currentPrompts.trim()) {
      setPrompts(stylePrompt);
      return;
    }
    
    // Split into lines for multi-line prompts
    const promptLines = currentPrompts.split('\n');
    
    // If the last line is selected (has cursor on it)
    // Just append to the last line
    const lastLine = promptLines[promptLines.length - 1];
    
    // Check if style is already in the prompt to avoid duplication
    if (!lastLine.includes(stylePrompt)) {
      // Add the style with a comma separator if needed
      const updatedLastLine = lastLine.trim().endsWith(',') || lastLine.trim() === '' 
        ? `${lastLine} ${stylePrompt}` 
        : `${lastLine}, ${stylePrompt}`;
      
      promptLines[promptLines.length - 1] = updatedLastLine;
      setPrompts(promptLines.join('\n'));
    }
  };

  // Function to improve prompt with AI
  const handleImprovePrompt = async () => {
    if (!prompts.trim()) {
      setError('Please enter a prompt to improve');
      return;
    }

    setIsImprovingPrompt(true);
    setError(null);
    setOriginalPrompt(prompts); // Store the original prompt

    try {
      // Call the AI model to improve the prompt
      const auxiliaryModel = getAuxiliaryModel();
      const improvedPromptResponse = await aiClient.generateArticleContent({
        title: prompts, // Using title parameter to pass our prompt
        modelId: auxiliaryModel,
        customPrompt: `Improve this image generation prompt to create a more detailed and visually appealing result. 
                Add specific details about style, lighting, composition, and quality.
                Keep the core subject and intent, but make it more descriptive.
                
                Original prompt: "[title]"
                
                Improved prompt:`
      });
      
      if (improvedPromptResponse) {
        // Extract the improved prompt and clean it up
        let improvedPrompt = improvedPromptResponse.trim();
        
        // Remove quotes if present
        if (improvedPrompt.startsWith('"') && improvedPrompt.endsWith('"')) {
          improvedPrompt = improvedPrompt.slice(1, -1);
        }
        
        setImprovedPrompt(improvedPrompt);
      } else {
        throw new Error('No improved prompt received');
      }
    } catch (err) {
      setError('Failed to improve prompt. Please try again.');
      console.error('Error improving prompt:', err);
    } finally {
      setIsImprovingPrompt(false);
    }
  };

  // Add function to accept the improved prompt
  const handleAcceptImprovedPrompt = () => {
    setPrompts(improvedPrompt);
    setImprovedPrompt(null);
    setOriginalPrompt('');
    setPromptManuallyAccepted(true); // Flag that the prompt was manually accepted
  };

  // Add function to reject the improved prompt
  const handleRejectImprovedPrompt = () => {
    setImprovedPrompt(null);
    setOriginalPrompt('');
  };

  // Add a function to handle image zooming
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
    setImagePosition({ x: 0, y: 0 }); // Reset position when zooming
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 1));
    setImagePosition({ x: 0, y: 0 }); // Reset position when zooming
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      setImagePosition(prev => ({ 
        x: prev.x + deltaX, 
        y: prev.y + deltaY 
      }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  const handleReset = () => {
    setPrompts('');
    setModel('fal-ai/flux/schnell');
    setDimension('square_hd');
    setUseAIEnhancement(true); // Re-add reset logic
    setSafetyCheckerEnabled(true);
    setNegativePrompt('');
    setShowPromptAssistant(false);
    setBulkMode(false);
    setBulkPrompts([{ id: Date.now(), prompt: '', model: imageModels[0].value, dimension: dimension }]);
    setSelectedImages([]);
    setGenerationProgress({ current: 0, total: 0 });
    setGeneratedImage(null);
    setUploadDialogOpen(false);
    setSelectedSite('');
    setUploadLoading(false);
    setUploadError(null);
    setUploadProgress({ current: 0, total: 0 });
    setDownloadProgress({ current: 0, total: 0 });
    setCompressionDialogOpen(false);
    setCompressionAction('download');
    setPendingImages([]);
    setPreviewImage(null);
    setIsImprovingPrompt(false);
    setPromptManuallyAccepted(false); // Reset manual acceptance flag
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };
  
  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        px: 3,
        pb: 2,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '96%',
          height: '1px',
          background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.01), rgba(59, 130, 246, 0.2) 20%, rgba(59, 130, 246, 0.2) 80%, rgba(59, 130, 246, 0.01))',
          borderRadius: '1px',
        }
      }}>
        <PageHeader
          category="Content Creation"
          title="AI Image Generator"
          description="Generate high-quality AI images from text descriptions that are perfect for creating featured images, illustrations, or social media post content. Create single images or batches of up to 50 images at once."
          icon={<Sparkles size={24} color="#E04C16" />}
        />
        <Box sx={{ mr: 6 }}>
        <Button
          onClick={() => setHelpDialogOpen(true)}
          startIcon={<HelpCircle size={18} />}
          sx={{
              bgcolor: 'rgba(224, 76, 22, 0.08)',
              color: '#E04C16',
              borderRadius: '12px',
              px: 3,
              py: 1,
              fontWeight: 600,
              fontSize: '0.875rem',
              border: '1px solid rgba(224, 76, 22, 0.12)',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease-in-out',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
              '&:hover': {
                  bgcolor: 'rgba(224, 76, 22, 0.12)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(224, 76, 22, 0.12)'
              }
          }}
        >
          How to Use
        </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3} sx={{ px: 2 }}>
        {/* Left Column - Image Settings */}
        <Grid item xs={12} md={6}>
        {/* Prompt Card */}
          <Paper sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            background: 'linear-gradient(to bottom, #FFFFFF, #FAFBFF)'
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: '#1F2937',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <ImageIcon size={18} color="#E04C16" />
                {bulkMode ? "Bulk Image Generation" : "Image Description"}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Single</Typography>
                <Switch 
                  checked={bulkMode}
                  onChange={(e) => {
                    setBulkMode(e.target.checked);
                    // Reset any ongoing generation when switching modes
                    if (loading) {
                      setLoading(false);
                    }
                  }}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">Bulk</Typography>
                <Tooltip title="Switch between single image and bulk generation modes">
                  <HelpCircle size={16} color="#6B7280" />
                </Tooltip>
              </Box>
            </Box>
            
            {!bulkMode ? (
              // Single image mode - existing UI
              <>
            <TextField
              fullWidth
                  label={!useAIEnhancement ? "Direct Image Prompt(s)" : "Image Description(s)"}
              multiline
                  rows={6}
              value={prompts}
              onChange={(e) => setPrompts(e.target.value)}
              required
                  disabled={loading || isImprovingPrompt}
                  placeholder={!useAIEnhancement ? 
                "Enter exact prompt(s) for the AI model... (one per line for multiple images)" : 
                "Describe the image(s) you want to create... (one per line for multiple images)"
              }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.12)',
                        transition: 'all 0.2s ease'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(59, 130, 246, 0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3B82F6',
                        borderWidth: '1px'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                      '&.Mui-focused': {
                        color: '#3B82F6'
                      }
                    }
                  }}
                />
                
                {/* AI Enhancement Toggle - Remove this section */}
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleImprovePrompt}
                    disabled={loading || isImprovingPrompt || !prompts.trim()}
                    startIcon={isImprovingPrompt ? <CircularProgress size={20} /> : <Sparkles size={20} />}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      padding: '8px 16px',
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                      color: '#6B7280',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        color: '#3B82F6',
                        backgroundColor: 'rgba(59, 130, 246, 0.04)'
                      }
                    }}
                  >
                    {isImprovingPrompt ? 'Improving...' : 'Improve with AI'}
                  </Button>
                </Box>

                {/* Improved Prompt Display */}
                {improvedPrompt && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#6B7280' }}>
                      AI-Improved Prompt
                    </Typography>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2, 
                        borderRadius: '12px',
                        backgroundColor: 'rgba(59, 130, 246, 0.04)',
                        border: '1px solid rgba(59, 130, 246, 0.1)'
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {improvedPrompt}
                      </Typography>
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleRejectImprovedPrompt}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            color: '#6B7280',
                            '&:hover': {
                              borderColor: '#EF4444',
                              color: '#EF4444',
                              backgroundColor: 'rgba(239, 68, 68, 0.04)'
                            }
                          }}
                        >
                          Reject
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleAcceptImprovedPrompt}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            background: 'linear-gradient(45deg, #3B82F6, #2563EB)',
                            '&:hover': {
                              background: 'linear-gradient(45deg, #2563EB, #1D4ED8)'
                            }
                          }}
                        >
                          Accept
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </>
            ) : (
              // Bulk image mode - new UI
              <Box sx={{ mt: 2 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2 
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4B5563' }}>
                    {bulkPrompts.length} {bulkPrompts.length === 1 ? 'Prompt' : 'Prompts'} in Queue
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<Plus size={16} />}
                      onClick={() => {
                        setBulkPrompts([
                          ...bulkPrompts, 
                          { 
                            id: Date.now(), 
                            prompt: '', 
                            model: imageModels[0].value, 
                            dimension: dimension 
                          }
                        ]);
                      }}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        color: '#3B82F6',
                        '&:hover': {
                          bgcolor: 'rgba(59, 130, 246, 0.04)'
                        }
                      }}
                    >
                      Add Prompt
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Upload size={16} />}
                      onClick={() => fileInputRef.current.click()}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        color: '#3B82F6',
                        '&:hover': {
                          bgcolor: 'rgba(59, 130, 246, 0.04)'
                        }
                      }}
                    >
                      Import CSV
                    </Button>
                    {bulkPrompts.length > 0 && (
                      <Button
                        size="small"
                        startIcon={<Trash2 size={16} />}
                        onClick={() => {
                          if (window.confirm('Are you sure you want to clear all prompts?')) {
                            setBulkPrompts([{ id: Date.now(), prompt: '', model: imageModels[0].value, dimension: dimension }]);
                          }
                        }}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          color: '#EF4444',
                          '&:hover': {
                            bgcolor: 'rgba(239, 68, 68, 0.04)'
                          }
                        }}
                      >
                        Clear All
                      </Button>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ 
                  maxHeight: '400px', 
                  overflowY: 'auto',
                  pr: 1,
                  pb: 1,
                  mt: 2
                }}>
                  {bulkPrompts.map((promptItem, index) => (
                    <Paper
                      key={promptItem.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: '10px',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                          borderColor: 'rgba(59, 130, 246, 0.3)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                          Prompt #{index + 1}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            size="small"
                            onClick={() => {
                              const newPrompts = [...bulkPrompts];
                              newPrompts.splice(index, 1);
                              if (newPrompts.length === 0) {
                                newPrompts.push({ id: Date.now(), prompt: '', model: imageModels[0].value, dimension: dimension });
                              }
                              setBulkPrompts(newPrompts);
                            }}
                            sx={{ 
                              color: '#EF4444',
                              p: 0.5
                            }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter image description..."
                        value={promptItem.prompt}
                        onChange={(e) => {
                          const newPrompts = [...bulkPrompts];
                          newPrompts[index].prompt = e.target.value;
                          setBulkPrompts(newPrompts);
                        }}
                        multiline
                        rows={2}
                        sx={{
                          mb: 1.5,
                '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '0.875rem'
                }
              }}
            />

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                          <InputLabel>Model</InputLabel>
                          <Select
                            value={promptItem.model}
                            label="Model"
                            onChange={(e) => {
                              const newPrompts = [...bulkPrompts];
                              newPrompts[index].model = e.target.value;
                              setBulkPrompts(newPrompts);
                            }}
                            sx={{ borderRadius: '8px', fontSize: '0.875rem' }}
                          >
                            {imageModels.map((m) => (
                              <MenuItem key={m.value} value={m.value}>
                                {m.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        
                        <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                          <InputLabel>Dimension</InputLabel>
                          <Select
                            value={promptItem.dimension}
                            label="Dimension"
                            onChange={(e) => {
                              const newPrompts = [...bulkPrompts];
                              newPrompts[index].dimension = e.target.value;
                              setBulkPrompts(newPrompts);
                            }}
                            sx={{ borderRadius: '8px', fontSize: '0.875rem' }}
                          >
                            <MenuItem value="square_hd">Square HD (1024×1024)</MenuItem>
                            <MenuItem value="portrait_4_3">Portrait 4:3 (768×1024)</MenuItem>
                            <MenuItem value="portrait_16_9">Portrait 16:9 (1024×1792)</MenuItem>
                            <MenuItem value="landscape_4_3">Landscape 4:3 (1024×768)</MenuItem>
                            <MenuItem value="landscape_16_9">Landscape 16:9 (1792×1024)</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Paper>
                  ))}
                </Box>
                
                {bulkPrompts.length === 0 && (
                  <Box sx={{ 
                    p: 4, 
                    textAlign: 'center', 
                    border: '2px dashed rgba(0, 0, 0, 0.1)',
                    borderRadius: '12px'
                  }}>
                    <Typography color="text.secondary">
                      No prompts added yet. Click "Add Prompt" to get started.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Prompt Building Assistant Toggle - Only in Single Mode */}
            {!bulkMode && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mt: 2,
                mb: 1
              }}>
                <Button
                  variant="text"
                  startIcon={<Lightbulb size={16} color={showPromptAssistant ? "#E04C16" : "#6B7280"} />}
                  onClick={() => setShowPromptAssistant(!showPromptAssistant)}
                  sx={{
                    color: showPromptAssistant ? "#E04C16" : "#6B7280",
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: 'rgba(224, 76, 22, 0.04)'
                    }
                  }}
                >
                  {showPromptAssistant ? "Hide Prompt Assistant" : "Show Prompt Assistant"}
                </Button>
              </Box>
            )}

            {/* Prompt Building Assistant - Only in Single Mode */}
            {!bulkMode && showPromptAssistant && (
              <Box sx={{ 
                mt: 1, 
                p: 2, 
                borderRadius: '12px', 
                bgcolor: 'rgba(224, 76, 22, 0.03)',
                border: '1px dashed rgba(224, 76, 22, 0.2)'
              }}>
                {/* Subject Categories */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1.5, 
                    fontWeight: 600, 
                    color: '#4B5563',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <ImageIcon size={16} color="#E04C16" />
                    Subject Categories
                    <Tooltip title="Click to add a subject to your prompt">
                      <HelpCircle size={14} color="#9CA3AF" style={{ marginLeft: '4px' }} />
                    </Tooltip>
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1,
                    maxWidth: '100%',
                    pb: 1
                  }}>
                    {subjectCategories.map(subject => (
                      <Chip 
                        key={subject.id}
                        label={subject.name}
                        onClick={() => appendSubjectToPrompt(subject.prompt)}
                        sx={{ 
                          borderRadius: '8px',
                          bgcolor: `${subject.color}15`,
                          color: subject.color,
                          border: `1px solid ${subject.color}30`,
                          fontWeight: 500,
                          py: 0.5,
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: `${subject.color}25`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 2px 8px ${subject.color}20`
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                
                {/* Lighting & Mood */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1.5, 
                    fontWeight: 600, 
                    color: '#4B5563',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Sun size={16} color="#E04C16" />
                    Lighting & Mood
                    <Tooltip title="Click to add lighting or mood to your prompt">
                      <HelpCircle size={14} color="#9CA3AF" style={{ marginLeft: '4px' }} />
                    </Tooltip>
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1,
                    maxWidth: '100%',
                    pb: 1
                  }}>
                    {lightingMoods.map(lighting => (
                      <Chip 
                        key={lighting.id}
                        label={lighting.name}
                        onClick={() => appendLightingToPrompt(lighting.prompt)}
                        sx={{ 
                          borderRadius: '8px',
                          bgcolor: `${lighting.color}15`,
                          color: lighting.color,
                          border: `1px solid ${lighting.color}30`,
                          fontWeight: 500,
                          py: 0.5,
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: `${lighting.color}25`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 2px 8px ${lighting.color}20`
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                
                {/* Negative Prompt */}
                <Box>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1.5, 
                    fontWeight: 600, 
                    color: '#4B5563',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <AlertCircle size={16} color="#EF4444" />
                    Negative Prompt
                    <Tooltip title="Specify what you DON'T want in the image">
                      <HelpCircle size={14} color="#9CA3AF" style={{ marginLeft: '4px' }} />
                    </Tooltip>
                  </Typography>
                  
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="E.g., blurry, low quality, distorted face, extra limbs"
                    value={negativePrompt}
                    onChange={handleNegativePromptChange}
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '& fieldset': {
                          borderColor: 'rgba(239, 68, 68, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(239, 68, 68, 0.4)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#EF4444',
                          borderWidth: '1px'
                        }
                      }
                    }}
                    helperText="Specify elements you want to exclude from the generated image"
                  />
                </Box>
              </Box>
            )}

            {/* Style Presets Section - Only in Single Mode */}
            {!bulkMode && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ 
                  mb: 1.5, 
                  fontWeight: 600, 
                  color: '#4B5563',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Palette size={16} color="#E04C16" />
                  Style Presets
                  <Tooltip title="Click on a style to add it to your prompt">
                    <HelpCircle size={14} color="#9CA3AF" style={{ marginLeft: '4px' }} />
                  </Tooltip>
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 1,
                  maxWidth: '100%',
                  overflowX: 'auto',
                  pb: 1
                }}>
                  {stylePresets.map(style => (
                    <Chip 
                      key={style.id}
                      avatar={<Avatar>{style.icon}</Avatar>}
                      label={style.name}
                      onClick={() => appendStyleToPrompt(style.prompt)}
                      sx={{ 
                        borderRadius: '8px',
                        bgcolor: `${style.color}15`,
                        color: style.color,
                        border: `1px solid ${style.color}30`,
                        fontWeight: 500,
                        py: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': { 
                          bgcolor: `${style.color}25`,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 2px 8px ${style.color}20`
                        },
                        '& .MuiChip-avatar': {
                          bgcolor: 'transparent',
                          color: 'inherit'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Replace the existing checkbox section with the corrected one */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 3, 
              mt: 3,
              alignItems: 'stretch'
            }}>
              {/* Enhance with AI checkbox block */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1,
                p: 2,
                borderRadius: '12px',
                bgcolor: useAIEnhancement ? 'rgba(59, 130, 246, 0.08)' : 'rgba(255, 255, 255, 0.8)',
                border: '1px solid',
                borderColor: useAIEnhancement ? 'rgba(59, 130, 246, 0.2)' : 'rgba(0, 0, 0, 0.06)',
                boxShadow: useAIEnhancement ? '0 2px 8px rgba(59, 130, 246, 0.08)' : 'none',
                transition: 'all 0.2s ease-in-out',
                flex: 1,
                minWidth: '240px',
                '&:hover': {
                  bgcolor: useAIEnhancement ? 'rgba(59, 130, 246, 0.12)' : 'rgba(255, 255, 255, 0.9)',
                  borderColor: useAIEnhancement ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.1)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                }
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useAIEnhancement}
                      onChange={(e) => setUseAIEnhancement(e.target.checked)}
                      disabled={loading}
                      sx={{
                        color: '#3B82F6',
                        padding: '4px',
                        '&.Mui-checked': {
                          color: '#3B82F6',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: 24,
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        Enhance with AI
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280', mt: 0.5 }}>
                        {useAIEnhancement 
                          ? "Prompts will be enhanced using AI" 
                          : "Prompts will be used exactly as written"}
                      </Typography>
                    </Box>
                  }
                  sx={{ mr: 0, alignItems: 'flex-start' }}
                />
                <Tooltip title={
                  useAIEnhancement
                    ? "When checked, the AI will automatically enhance your descriptions during image generation"
                    : "When unchecked, your prompt will be used exactly as written during image generation (no automatic enhancement)"
                }>
                  <HelpCircle size={16} color="#6B7280" style={{ marginTop: '4px' }} />
                </Tooltip>
              </Box>

              {/* Content safety filter block */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1,
                p: 2,
                borderRadius: '12px',
                bgcolor: safetyCheckerEnabled ? 'rgba(59, 130, 246, 0.08)' : 'rgba(255, 255, 255, 0.8)',
                border: '1px solid',
                borderColor: safetyCheckerEnabled ? 'rgba(59, 130, 246, 0.2)' : 'rgba(0, 0, 0, 0.06)',
                boxShadow: safetyCheckerEnabled ? '0 2px 8px rgba(59, 130, 246, 0.08)' : 'none',
                transition: 'all 0.2s ease-in-out',
                flex: 1,
                minWidth: '240px',
                '&:hover': {
                  bgcolor: safetyCheckerEnabled ? 'rgba(59, 130, 246, 0.12)' : 'rgba(255, 255, 255, 0.9)',
                  borderColor: safetyCheckerEnabled ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.1)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                }
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={safetyCheckerEnabled}
                      onChange={(e) => setSafetyCheckerEnabled(e.target.checked)}
                      disabled={loading}
                      sx={{
                        color: '#3B82F6',
                        padding: '4px',
                        '&.Mui-checked': {
                          color: '#3B82F6',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: 24,
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        Content safety filter
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280', mt: 0.5 }}>
                        {safetyCheckerEnabled 
                          ? "Filter inappropriate content" 
                          : "Basic filtering still applied"}
                      </Typography>
                    </Box>
                  }
                  sx={{ mr: 0, alignItems: 'flex-start' }}
                />
                <Tooltip title={
                  safetyCheckerEnabled 
                    ? "When checked, inappropriate content will be blocked" 
                    : "Basic content filtering will still be applied"
                }>
                  <HelpCircle size={16} color="#6B7280" style={{ marginTop: '4px' }} />
                </Tooltip>
              </Box>
            </Box>
          </Paper> 

        {/* Settings Card */}
          {!bulkMode && (
            <Paper sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              background: 'linear-gradient(to bottom, #FFFFFF, #FAFBFF)'
            }}>
              <Typography variant="h6" sx={{ 
                mb: 2, 
                fontWeight: 600, 
                color: '#1F2937',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Settings size={18} color="#3B82F6" />
                Image Settings
              </Typography>
            
            <ImageControls
              model={model}
              setModel={setModel}
              dimension={dimension}
              setDimension={setDimension}
              loading={loading}
            />

            {/* Error Display */}
            {(error || authError || modelsError) && (
              <Alert 
                severity="error" 
                sx={{
                    mt: 2,
                    borderRadius: '12px',
                  backgroundColor: 'rgba(211, 47, 47, 0.05)'
                }}
              >
                {error || authError || modelsError}
              </Alert>
            )}

              {/* Generate Button for Single Mode */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<RefreshCw size={20} />}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    padding: '12px 24px',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    color: '#6B7280',
                    '&:hover': {
                      borderColor: '#3B82F6',
                      color: '#3B82F6',
                      backgroundColor: 'rgba(59, 130, 246, 0.04)'
                    }
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  disabled={loading || !prompts.trim()}
                  onClick={handleGenerate}
                  startIcon={loading ? <CircularProgress size={20} /> : <ImageIcon size={20} />}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    padding: '12px 24px',
                    background: 'linear-gradient(45deg, #3B82F6, #2563EB)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #2563EB, #1D4ED8)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(45deg, #E5E7EB, #F3F4F6)',
                      boxShadow: 'none'
                    }
                  }}
                >
                  {loading ? 
                    `Generating${generationProgress.total > 1 ? 
                      ` (${generationProgress.current}/${generationProgress.total})` : 
                      '...'
                    }` : 
                    'Generate Image(s)'
                  }
                </Button>
              </Box>
            </Paper>
          )}

          {/* Generate Button for Bulk Mode */}
          {bulkMode && (
            <Paper sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              background: 'linear-gradient(to bottom, #FFFFFF, #FAFBFF)'
            }}>
              {/* Error Display */}
              {(error || authError || modelsError) && (
                <Alert 
                  severity="error" 
                  sx={{
                    mb: 2,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(211, 47, 47, 0.05)'
                  }}
                >
                  {error || authError || modelsError}
                </Alert>
              )}

              {/* Generate Button for Bulk Mode */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  disabled={loading || bulkPrompts.filter(item => item.prompt.trim()).length === 0}
                  onClick={handleGenerate}
                  startIcon={loading ? <CircularProgress size={20} /> : <ImageIcon size={20} />}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    padding: '12px 24px',
                    background: 'linear-gradient(45deg, #3B82F6, #2563EB)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #2563EB, #1D4ED8)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(45deg, #E5E7EB, #F3F4F6)',
                      boxShadow: 'none'
                    }
                  }}
                >
                  {loading ? 
                    `Generating${generationProgress.total > 1 ? 
                      ` (${generationProgress.current}/${generationProgress.total})` : 
                      '...'
                    }` : 
                    `Generate ${bulkPrompts.filter(item => item.prompt.trim()).length} Image(s)`
                  }
                </Button>
          </Box>
        </Paper>
          )}
        </Grid>

        {/* Right Column - Preview & Results */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3, 
            height: '100%',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            background: 'linear-gradient(to bottom, #FFFFFF, #F8FAFC)',
            position: 'sticky',
            top: '24px'
          }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#1F2937',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&::before': {
                  content: '""',
                  width: '3px',
                  height: '18px',
                  backgroundColor: '#E04C16',
                  borderRadius: '2px',
                  display: 'inline-block'
                }
              }}
            >
              Generated Images
            </Typography>

        {/* Images Display */}
            {generatedImages.length > 0 ? (
              generatedImages.length === 1 ? (
  // Single Image Large Preview
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {generatedImages[0].status === 'success' ? (
      <>
                      <Box sx={{ 
                        position: 'relative',
                        width: '100%',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
                      }}>
          <img
            src={generatedImages[0].url}
            alt={generatedImages[0].prompt}
                          style={{ 
                            width: '100%', 
                            height: 'auto', 
                            display: 'block'
                          }}
                          onClick={() => handleOpenImageDetails(generatedImages[0])}
                        />
                      </Box>
                      <Box sx={{ 
                        mt: 1, 
                        p: 2, 
                        borderRadius: '10px', 
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        border: '1px solid rgba(0, 0, 0, 0.05)'
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                          "{generatedImages[0].prompt}"
                        </Typography>
                        
                        {/* Add model and dimension info */}
                        {(generatedImages[0].model || generatedImages[0].dimension) && (
                          <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 1, 
                            mt: 1 
                          }}>
                            {generatedImages[0].model && (
                              <Chip
                                label={`Model: ${generatedImages[0].model.split('/').pop()}`}
                                size="small"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  borderRadius: 1,
                                  bgcolor: 'rgba(59, 130, 246, 0.1)',
                                  color: '#3B82F6'
                                }}
                              />
                            )}
                            {generatedImages[0].dimension && (
                              <Chip
                                label={`Dimension: ${generatedImages[0].dimension.replace('_', ' ')}`}
                                size="small"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  borderRadius: 1,
                                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                                  color: '#10B981'
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 1 }}>
                      {wordPressSites.length > 0 && (
                        <Button
                          variant="contained"
                            startIcon={<Upload size={18} />}
                          onClick={() => {
                            setPendingImages([generatedImages[0]]);
                            setUploadProgress({ current: 0, total: 1 });
                            setCompressionAction('upload');
                            setCompressionDialogOpen(true);
                          }}
                            sx={{
                              borderRadius: '10px',
                              textTransform: 'none',
                              fontWeight: 500,
                              bgcolor: '#3B82F6',
                              '&:hover': {
                                bgcolor: '#2563EB'
                              }
                          }}
                        >
                          Upload to WordPress
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                          startIcon={<Download size={18} />}
                        onClick={() => {
                          setPendingImages([generatedImages[0]]);
                          setCompressionAction('download');
                          setCompressionDialogOpen(true);
                        }}
                          sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 500,
                            borderColor: 'rgba(59, 130, 246, 0.5)',
                            color: '#3B82F6',
                            '&:hover': {
                              borderColor: '#3B82F6',
                              bgcolor: 'rgba(59, 130, 246, 0.04)'
                            }
                        }}
                      >
                        Download Image
                      </Button>
                      </Box>
      </>
    ) : (
                    <Box sx={{ 
                      p: 3, 
                      borderRadius: '12px', 
                      bgcolor: 'rgba(239, 68, 68, 0.05)',
                      border: '1px solid rgba(239, 68, 68, 0.1)'
                    }}>
        <Typography color="error">
          Generation failed: {generatedImages[0].error}
        </Typography>
      </Box>
    )}
                </Box>
) : (
              // Multi-Image Grid View
              <>
                  <Box sx={{ 
                    mb: 3, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    pb: 2,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4B5563' }}>
                      {generatedImages.length} Images Generated
                  </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
  <Button
    variant="outlined"
                        size="small"
    onClick={handleSelectAll}
    disabled={loading}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          py: 0.5,
                          borderColor: 'rgba(107, 114, 128, 0.3)',
                          color: '#6B7280',
                          '&:hover': {
                            borderColor: '#6B7280',
                            bgcolor: 'rgba(107, 114, 128, 0.04)'
                          }
                        }}
  >
    {selectedImages.length === generatedImages.length ? 'Deselect All' : 'Select All'}
  </Button>
  <Button
    variant="contained"
                        size="small"
                        startIcon={<Download size={14} />}
    disabled={selectedImages.length === 0 || loading}
    onClick={() => {
      const selectedImagesToDownload = generatedImages.filter(
        img => selectedImages.includes(img.id) && img.status === 'success'
      );
      setPendingImages(selectedImagesToDownload);
      setCompressionAction('download');
      setCompressionDialogOpen(true);
    }}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          py: 0.5,
                          bgcolor: '#3B82F6',
                          '&:hover': {
                            bgcolor: '#2563EB'
                          }
                        }}
                      >
                        Download
  </Button>
  {wordPressSites.length > 0 && (
    <Button
      variant="contained"
                          size="small"
                          startIcon={<Upload size={14} />}
      disabled={selectedImages.length === 0 || loading}
      onClick={() => {
        const selectedImagesToUpload = generatedImages.filter(
          img => selectedImages.includes(img.id) && img.status === 'success'
        );
        setPendingImages(selectedImagesToUpload);
        setUploadProgress({ current: 0, total: selectedImagesToUpload.length }); 
        setCompressionAction('upload');
        setCompressionDialogOpen(true);
      }}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            py: 0.5,
                            bgcolor: '#3B82F6',
                            '&:hover': {
                              bgcolor: '#2563EB'
                            }
                          }}
                        >
                          Upload
    </Button>
  )}
</Box>
                </Box>

                  <Box sx={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto', pr: 1 }}>
                <Grid container spacing={2}>
                  {generatedImages.map((image) => (
                        <Grid item xs={6} sm={6} key={image.id}>
                      <Card 
                            sx={{ 
                              position: 'relative',
                              borderRadius: '10px',
                              overflow: 'hidden',
                              transition: 'all 0.2s ease-in-out',
                              border: selectedImages.includes(image.id) ? '2px solid #3B82F6' : '1px solid rgba(0, 0, 0, 0.08)',
                              boxShadow: selectedImages.includes(image.id) ? '0 0 0 2px rgba(59, 130, 246, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                transform: 'translateY(-2px)'
                              }
                            }}
                      >
                        {image.status === 'success' ? (
                              <>
                          <CardMedia
                            component="img"
                                  height="140"
                            image={image.url}
                            alt={image.prompt}
                                  sx={{ 
                                    cursor: 'pointer',
                                    objectFit: 'cover'
                                  }}
                                  onClick={() => {
                                    handleSelectImage(image.id);
                                    handleOpenImageDetails(image);
                                  }}
                                />
                                <CardContent sx={{ p: 1.5, pb: '8px !important' }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      display: '-webkit-box',
                                      overflow: 'hidden',
                                      WebkitBoxOrient: 'vertical',
                                      WebkitLineClamp: 2,
                                      fontSize: '0.75rem',
                                      color: '#4B5563'
                                    }}
                                  >
                            {image.prompt}
                          </Typography>
                        </CardContent>
                        <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    display: 'flex',
                                    gap: 0.5,
                                    zIndex: 2
                                  }}
                          onClick={(e) => e.stopPropagation()}
                        >
                              <IconButton
                                size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectImage(image.id);
                                    }}
                                    sx={{
                                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                                      backdropFilter: 'blur(4px)',
                                      width: 28,
                                      height: 28,
                                      '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 1)'
                                      }
                                    }}
                              >
                                {selectedImages.includes(image.id) ? (
                                      <CheckCircle size={16} color="#3B82F6" />
                                ) : (
                                      <ImageIcon size={16} color="#6B7280" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenImageDetails(image);
                                    }}
                                    sx={{
                                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                                      backdropFilter: 'blur(4px)',
                                      width: 28,
                                      height: 28,
                                      '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 1)'
                                      }
                                    }}
                                  >
                                    <HelpCircle size={16} color="#3B82F6" />
                              </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewImage(image);
                                    }}
                                    sx={{
                                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                                      backdropFilter: 'blur(4px)',
                                      width: 28,
                                      height: 28,
                                      '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 1)'
                                      }
                                    }}
                                  >
                                    <Maximize size={16} color="#6B7280" />
                                  </IconButton>
                                </Box>
                              </>
                            ) : (
                              <Box sx={{ 
                                height: 140, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                bgcolor: 'rgba(239, 68, 68, 0.05)'
                              }}>
                                <Typography color="error" variant="caption" sx={{ px: 2, textAlign: 'center' }}>
                                  Generation failed
                                </Typography>
                        </Box>
                            )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                  </Box>
                </>
              )
            ) : !loading ? (
              <Box sx={{ 
                height: '400px', 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                border: '2px dashed rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                p: 4
              }}>
                <ImageIcon size={48} color="#E5E7EB" />
                <Typography sx={{ mt: 2, color: '#9CA3AF', textAlign: 'center' }}>
                  Generated images will appear here
            </Typography>
                <Typography variant="caption" sx={{ mt: 1, color: '#9CA3AF', textAlign: 'center' }}>
                  Enter your image descriptions and click "Generate Image(s)" to start
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                height: '400px', 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                border: '2px dashed rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                p: 4,
                bgcolor: 'rgba(59, 130, 246, 0.02)'
              }}>
                <CircularProgress size={40} sx={{ color: '#3B82F6' }} />
                <Typography sx={{ mt: 3, color: '#3B82F6', fontWeight: 500 }}>
                  Generating images...
                </Typography>
                {generationProgress.total > 1 && (
                  <Typography variant="caption" sx={{ mt: 1, color: '#6B7280' }}>
                    {generationProgress.current} of {generationProgress.total} images
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Hidden file input for CSV import */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".csv"
        onChange={handleCsvImport}
      />

      {/* Help Dialog */}
      <Dialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            How to Use the Image Generator
          </Typography>
          <IconButton onClick={() => setHelpDialogOpen(false)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#E04C16', fontWeight: 600 }}>
              Step 1: Describe Your Image
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Enter a detailed description of the image you want to create. The more specific your description, the better the results will be.
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'rgba(59, 130, 246, 0.04)', borderRadius: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                Example: "A majestic mountain landscape with snow-capped peaks, pine trees in the foreground, and a crystal clear lake reflecting the blue sky with some clouds"
              </Typography>
            </Paper>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Toggle AI Enhancement to have our system automatically improve your prompt.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#E04C16', fontWeight: 600 }}>
              Step 2: Select Model & Dimensions
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Choose an AI model based on your quality needs and desired resolution. Each model has different capabilities and price points.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • Select appropriate dimensions for your image (square, portrait, or landscape)
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              • Models with higher capabilities can produce more detailed images at larger resolutions
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#E04C16', fontWeight: 600 }}>
              Step 3: Enhance Your Prompt
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Use the style presets, subject categories, and lighting options to quickly enhance your prompt with professional aesthetics.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Click any of these buttons to automatically append quality terms to your prompt.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#E04C16', fontWeight: 600 }}>
              Step 4: Generate & Download
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Click the "Generate" button to create your image. Once complete, you can download it, or upload directly to WordPress.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              For bulk generation, toggle to "Bulk" mode and enter one prompt per line.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#E04C16', fontWeight: 600 }}>
              Bulk Generation & CSV Upload
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              For creating multiple images at once, use the Bulk Generation mode:
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • Toggle to "Bulk" mode at the top of the prompt section
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • Add multiple prompts manually or import from a CSV file
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • Click "Generate" to create all images at once
            </Typography>
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              CSV Import Format:
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'rgba(59, 130, 246, 0.04)', borderRadius: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                prompt,model,dimension
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                mountain landscape,fal-ai/flux/schnell,square_hd
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                portrait of a woman,fal-ai/flux-pro/v1.1-ultra,portrait_4_3
              </Typography>
            </Paper>
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              CSV Requirements:
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • CSV can include just prompts (one per line) or include model and dimension
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • Headers are optional (prompt,model,dimension)
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • Valid dimensions: square_hd, portrait_4_3, portrait_16_9, landscape_4_3, landscape_16_9
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • If model or dimension is invalid, defaults will be used
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              • Maximum of 50 prompts recommended per batch
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 1, color: '#E04C16', fontWeight: 600 }}>
              Tips for Better Results
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • Be specific about lighting, style, perspective, and mood
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • Include details about colors, textures, and composition
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              • Use photographic terms like "wide angle" or "macro" when appropriate
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              • For best results with text in images, use the Ideogram models
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
          <Button 
            onClick={() => setHelpDialogOpen(false)}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              bgcolor: '#E04C16',
              '&:hover': {
                bgcolor: '#C73C10'
              }
            }}
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
  open={notification.open}
  autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
>
  <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
    severity={notification.severity}
          sx={{ 
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            width: '100%'
          }}
  >
    {notification.message}
  </Alert>
</Snackbar>

      {/* Compression Dialog */}
      <CompressionDialog
        open={compressionDialogOpen}
        onClose={() => setCompressionDialogOpen(false)}
        onDownload={handleDownloadWithCompression}
        onUpload={handleUploadWithCompression}
        action={compressionAction}
        wordPressSites={wordPressSites}
        selectedSite={selectedSite}
        setSelectedSite={setSelectedSite}
        uploadLoading={uploadLoading}
        uploadProgress={uploadProgress}
        downloadLoading={loading && compressionAction === 'download' && downloadProgress.total > 0}
        downloadProgress={downloadProgress}
      />

      {/* Image Preview Dialog */}
<Dialog 
        open={!!previewImage}
        onClose={() => {
          setPreviewImage(null);
          setZoomLevel(1);
          setImagePosition({ x: 0, y: 0 });
        }}
        maxWidth="xl"
        fullWidth
  PaperProps={{
    sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            height: '90vh',
            maxHeight: '90vh',
            m: 2
          }
        }}
      >
        <DialogTitle sx={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Image Preview
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handleZoomOut} disabled={zoomLevel <= 1}>
              <ZoomOut size={20} />
            </IconButton>
            <Typography sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
              {Math.round(zoomLevel * 100)}%
            </Typography>
            <IconButton onClick={handleZoomIn} disabled={zoomLevel >= 3}>
              <ZoomIn size={20} />
            </IconButton>
            <IconButton onClick={handleZoomReset} disabled={zoomLevel === 1}>
              <Maximize size={20} />
            </IconButton>
            <IconButton onClick={() => {
              setPreviewImage(null);
              setZoomLevel(1);
              setImagePosition({ x: 0, y: 0 });
            }}>
              <X size={20} />
    </IconButton>
  </Box>
        </DialogTitle>
        <DialogContent 
          sx={{ 
            p: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            overflow: 'hidden',
            bgcolor: '#f5f5f5',
            cursor: zoomLevel > 1 ? 'grab' : 'default',
            '&:active': {
              cursor: zoomLevel > 1 ? 'grabbing' : 'default'
            }
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {previewImage && (
            <Box sx={{ 
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={previewImage.url}
                alt={previewImage.prompt}
                style={{ 
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transform: `scale(${zoomLevel}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                  transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setZoomLevel(1);
          setImagePosition({ x: 0, y: 0 });
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Image Details
    </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handleZoomOut} disabled={zoomLevel <= 1}>
              <ZoomOut size={20} />
            </IconButton>
            <Typography sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
              {Math.round(zoomLevel * 100)}%
    </Typography>
            <IconButton onClick={handleZoomIn} disabled={zoomLevel >= 3}>
              <ZoomIn size={20} />
            </IconButton>
            <IconButton onClick={handleZoomReset} disabled={zoomLevel === 1}>
              <Maximize size={20} />
            </IconButton>
            <IconButton onClick={() => {
              setDetailsDialogOpen(false);
              setZoomLevel(1);
              setImagePosition({ x: 0, y: 0 });
            }}>
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedImageDetails && (
            <Grid container>
              <Grid item xs={12} md={7} sx={{ 
                position: 'relative',
                height: '500px',
                overflow: 'hidden',
                bgcolor: '#f5f5f5',
                cursor: zoomLevel > 1 ? 'grab' : 'default',
                '&:active': {
                  cursor: zoomLevel > 1 ? 'grabbing' : 'default'
                }
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              >
                <Box sx={{ 
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img
                    src={selectedImageDetails.url}
                    alt={selectedImageDetails.prompt}
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '100%',
                      transform: `scale(${zoomLevel}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                      transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1F2937' }}>
                    Prompt
    </Typography>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      mb: 3, 
                      bgcolor: 'rgba(0, 0, 0, 0.02)', 
                      borderRadius: '10px',
                      border: '1px solid rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {selectedImageDetails.prompt}
    </Typography>
                  </Paper>

                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1F2937' }}>
                    Generation Details
    </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {selectedImageDetails.model && (
                        <Chip
                          icon={<Zap size={14} />}
                          label={`Model: ${selectedImageDetails.model.split('/').pop()}`}
                          size="small"
                          sx={{ 
                            borderRadius: '8px',
                            bgcolor: 'rgba(59, 130, 246, 0.1)',
                            color: '#3B82F6',
                            '& .MuiChip-icon': { color: '#3B82F6' }
                          }}
                        />
                      )}
                      {selectedImageDetails.dimension && (
                        <Chip
                          icon={<Maximize size={14} />}
                          label={`Dimension: ${selectedImageDetails.dimension.replace('_', ' ')}`}
                          size="small"
                          sx={{ 
                            borderRadius: '8px',
                            bgcolor: 'rgba(16, 185, 129, 0.1)',
                            color: '#10B981',
                            '& .MuiChip-icon': { color: '#10B981' }
                          }}
                        />
                      )}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Created:</strong> {selectedImageDetails.timestamp ? new Date(selectedImageDetails.timestamp).toLocaleString() : 'Unknown'}
    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>AI Enhancement:</strong> {selectedImageDetails.enhancePrompt ? 'Enabled' : 'Disabled'}
    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      <strong>Safety Filter:</strong> {selectedImageDetails.safetyFilter ? 'Enabled' : 'Basic'}
    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
  </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
          <Button 
            variant="outlined" 
            onClick={() => setDetailsDialogOpen(false)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none'
            }}
          >
            Close
    </Button>
          <Button 
            variant="contained" 
            startIcon={<Download size={18} />}
            onClick={() => {
              if (selectedImageDetails) {
                setPendingImages([selectedImageDetails]);
                setCompressionAction('download');
                setCompressionDialogOpen(true);
                setDetailsDialogOpen(false);
              }
            }}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              bgcolor: '#3B82F6',
              '&:hover': {
                bgcolor: '#2563EB'
              }
            }}
          >
            Download
          </Button>
          {wordPressSites.length > 0 && (
            <Button 
              variant="contained" 
              startIcon={<Upload size={18} />}
              onClick={() => {
                if (selectedImageDetails) {
                  setPendingImages([selectedImageDetails]);
                  setUploadProgress({ current: 0, total: 1 });
                  setCompressionAction('upload');
                  setCompressionDialogOpen(true);
                  setDetailsDialogOpen(false);
                }
              }}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                bgcolor: '#3B82F6',
                '&:hover': {
                  bgcolor: '#2563EB'
                }
              }}
            >
              Upload to WordPress
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box> // Closing the main outer Box for the component
  );
};

export default ImageGenerator;